import { Container, Graphics, FillGradient } from '../../../vendor/pixi.min.mjs';

/**
 * Decorative sky background with smooth gradient, sun, and drifting clouds.
 */
export class FroggerSkyLayer {
    container = new Container();

    _clouds = [];
    _screenW = 0;
    _screenH = 0;
    _themeManager = null;

    /** Rays Graphics — rotates around the sun centre when spinning. */
    _sunRays = null;
    /** Per-frame rotation speed (radians/frame) once the win spin is engaged. */
    _sunSpinSpeed = 0;
    /** Target spin speed — eased toward to avoid an abrupt start. */
    _sunSpinTarget = 0;

    constructor(screenW, screenH, themeManager = null) {
        this._screenW = screenW;
        this._screenH = screenH;
        this._themeManager = themeManager;

        this._drawSky();
        this._drawSun();
        this._createClouds();
    }

    update(delta) {
        for (const c of this._clouds) {
            c.gfx.x += c.speed * delta;
            if (c.gfx.x - c.w > this._screenW) {
                c.gfx.x = -c.w;
            }
        }

        if (this._sunRays) {
            this._sunSpinSpeed += (this._sunSpinTarget - this._sunSpinSpeed) * 0.04;
            if (Math.abs(this._sunSpinSpeed) > 0.0001) {
                this._sunRays.rotation += this._sunSpinSpeed * delta;
            }
        }
    }

    /**
     * Engage the celebratory sun-ray rotation. Called when the player
     * reaches the museum so the sky reacts to the win.
     * @param {number} [speed=0.03] — radians per frame at full spin.
     */
    startSunSpin(speed = 0.03) {
        this._sunSpinTarget = speed;
    }

    /** Halt the spin (eases back to 0). */
    stopSunSpin() {
        this._sunSpinTarget = 0;
    }

    _drawSky() {
        const sprite = this._themeManager?.createSprite('frogger.sky', this._screenW, this._screenH, {
            anchorX: 0,
            anchorY: 0,
            fitMode: 'cover',
        });
        if (sprite) {
            this.container.addChild(sprite);
            return;
        }

        // The procedural sky gradient is always drawn as the fallback
        // backdrop. (An earlier experiment skipped it when a custom
        // `frogger.background` image was loaded so the bg could shine
        // through, but that path is currently disabled in
        // `FroggerScene._createThemeBackground` because it darkens the
        // stage. Re-enable both together if/when we revisit it.)
        const g = new Graphics();
        // Top of the screen stays sky-blue (only visible above the museum at
        // the finish). The middle and bottom transition into the road colour
        // so any pixels NOT covered by a generated row (left/right margins of
        // the grid, the visual gap directly under the museum, the area below
        // the lowest road row) match the road blue seamlessly instead of
        // showing a paler "sky" tint bleeding through.
        const grad = new FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0,    color: '#4a90d9' }, // sky top
                { offset: 0.32, color: '#5fa1de' }, // soft transition
                { offset: 0.55, color: '#3a91dc' }, // road blue from here down
                { offset: 1,    color: '#3a91dc' },
            ],
        });
        g.rect(0, 0, this._screenW, this._screenH);
        g.fill(grad);
        this.container.addChild(g);
    }

    _drawSun() {
        const cx = this._screenW - 90;
        const cy = 80;
        const r = 38;

        // Rays are drawn relative to their own origin so the graphic can
        // be rotated in place via `rays.rotation`. The pivot is implicit
        // at (0,0); we just translate the rays onto the sun centre.
        const rays = new Graphics();
        for (let i = 0; i < 8; i++) {
            const angle = (i / 8) * Math.PI * 2;
            const outerR = r + 30;
            const spread = 0.18;

            rays.moveTo(0, 0);
            rays.lineTo(
                Math.cos(angle - spread) * outerR,
                Math.sin(angle - spread) * outerR
            );
            rays.lineTo(
                Math.cos(angle + spread) * outerR,
                Math.sin(angle + spread) * outerR
            );
            rays.closePath();
            rays.fill({ color: 0xfde68a, alpha: 0.25 });
        }
        rays.x = cx;
        rays.y = cy;
        this.container.addChild(rays);
        this._sunRays = rays;

        // Sun body (halo + disc + highlight) — stays still while rays spin.
        const body = new Graphics();
        body.circle(cx, cy, r + 6);
        body.fill({ color: 0xfef3c7, alpha: 0.35 });

        body.circle(cx, cy, r);
        body.fill(0xfbbf24);

        body.circle(cx - 8, cy - 8, r * 0.55);
        body.fill({ color: 0xfef08a, alpha: 0.5 });

        this.container.addChild(body);
    }

    _createClouds() {
        const defs = [
            { x: 60,  y: 40,  scale: 1.0,  speed: 0.12 },
            { x: 350, y: 70,  scale: 0.7,  speed: 0.08 },
            { x: 600, y: 30,  scale: 0.85, speed: 0.15 },
            { x: 200, y: 100, scale: 0.6,  speed: 0.06 },
            { x: 800, y: 55,  scale: 0.75, speed: 0.10 },
        ];

        for (const d of defs) {
            const g = new Graphics();
            const s = d.scale;

            g.ellipse(0, 0, 60 * s, 22 * s);
            g.fill({ color: 0xffffff, alpha: 0.75 });

            g.ellipse(-30 * s, 4 * s, 36 * s, 18 * s);
            g.fill({ color: 0xffffff, alpha: 0.6 });

            g.ellipse(28 * s, 2 * s, 40 * s, 16 * s);
            g.fill({ color: 0xffffff, alpha: 0.65 });

            g.ellipse(-8 * s, -12 * s, 32 * s, 16 * s);
            g.fill({ color: 0xf8fafc, alpha: 0.7 });

            g.ellipse(18 * s, -8 * s, 28 * s, 14 * s);
            g.fill({ color: 0xf8fafc, alpha: 0.55 });

            g.x = d.x;
            g.y = d.y;
            this.container.addChild(g);

            this._clouds.push({ gfx: g, speed: d.speed, w: 120 * s });
        }
    }
}
